/**
 * The proof-of-control screen — the product's thesis, rendered.
 *
 * Two audiences, one ceremony, and they never share a machine:
 *
 *   PROVE  — you hold the wallet. You take an exact message to the wallet software that holds
 *            the key, sign it THERE, and paste the signature back. No private key is typed into
 *            this page, ever, and the page could not use one if you did.
 *   CHECK  — you were sent a proof. You need no wallet, no descriptor and no account: the bundle
 *            carries everything, and the verdict re-derives all of it.
 *
 * `check` is the half that needs nothing, deliberately — the person who most needs to test a
 * claim of control is the one with no relationship to the wallet making it.
 */
import { html, raw, type Raw } from '@signingroom/ui/dom';
import { state } from './state.js';
import { DEFAULT_TTL_MS, addressToSignWith } from './control.js';

const fmt = (ms: number): string => new Date(ms).toISOString().replace('T', ' ').slice(0, 16);

/** Where the reader actually goes to sign. Named, because "sign a message" is not a found menu. */
function whereToSign(): Raw {
  return html`
    <details class="disc" style="margin-top:12px">
      <summary class="label" style="color:var(--grey);cursor:pointer">
        Where do I sign this?</summary>
      <div class="sub" style="margin-top:9px;margin-bottom:0">
        <p style="margin-bottom:7px"><strong>Sparrow</strong> — Tools → Sign/Verify Message. Pick
          the address shown above, paste the message, press Sign.</p>
        <p style="margin-bottom:7px"><strong>Bitcoin Core</strong> — File → Sign message, or
          <code class="mono">signmessage "&lt;address&gt;" "&lt;message&gt;"</code> in the
          console.</p>
        <p style="margin-bottom:7px"><strong>Electrum</strong> — right-click the address in the
          Addresses tab → Sign/verify message.</p>
        <p style="margin-bottom:0"><strong>A hardware wallet</strong> — sign through whichever of
          the above drives your device. Support for message signing varies by model, and this
          page cannot test that for you.</p>
      </div>
    </details>`;
}

function proveView(): Raw {
  const c = state.control;
  const parsed = state.parsed;

  if (!parsed)
    return html`
      <div class="note">
        <div class="h">Name the wallet first</div>
        A proof of control says "I control <em>this</em> wallet", so this page has to know which
        wallet you mean. Paste its descriptor in <strong>Your wallet</strong> just below — it is
        watch-only and cannot spend anything.
      </div>`;

  const signAddr = addressToSignWith(parsed);
  return html`
    <div class="blk">
      <h4>1 · The challenge</h4>
      <p class="sub">
        A challenge is the question you are answering. Whoever wants proof should send you one —
        the randomness in it has to be <strong>theirs</strong>, or you could have prepared the
        answer in advance and it would show nothing.
      </p>
      <div class="field">
        <label class="lbl" for="ctl-challenge">Paste the challenge you were sent</label>
        <textarea id="ctl-challenge" rows="5" class="mono" spellcheck="false"
          placeholder='{ "vaultId": "...", "nonce": "...", ... }'>${c.challengeText}</textarea>
      </div>
      <div class="row">
        <button class="btn sm ghost" id="ctl-use-challenge">Use this challenge</button>
        <button class="btn sm ghost" id="ctl-issue">No one sent me one — make my own</button>
      </div>
      ${c.selfIssued && c.challenge ? html`
        <div class="cant" style="margin-top:14px">
          <div class="h">This is a challenge you made yourself</div>
          <p style="margin-bottom:0">It proves you controlled the key <em>when you made it</em>,
          which is enough for your own records. It is <strong>not</strong> enough for a
          counterparty who wants assurance today: they have no way to know you did not build it
          last year. For that, ask them to send you a challenge with their own randomness in it.</p>
        </div>` : raw('')}
    </div>

    ${c.challenge ? html`
      <div class="rule"></div>
      <div class="blk">
        <h4>2 · Sign this, in the wallet that holds the key</h4>
        <p class="sub">
          Copy it exactly — every character is covered by the signature, so a stray space makes an
          honest signature fail to verify. This page never sees your key.
        </p>
        <div class="hashline" id="ctl-message" style="white-space:pre-wrap">${c.message}</div>
        <div class="row" style="margin-top:10px">
          <button class="btn sm" id="ctl-copy-message">Copy the message</button>
          <span class="sub" style="margin:0">Answerable until ${fmt(c.challenge.expiresAt)} UTC</span>
        </div>
        ${/*
           * The address is SHOWN. "Pick the address shown above" used to point at nothing, and
           * step 3 then asked which address was used — the one question in this ceremony a
           * holder could not answer from the screen. Any of the wallet's own addresses verifies;
           * the first receiving address is the one every wallet puts on its receive screen.
           */''}
        <div class="field" style="margin-top:14px">
          <label class="lbl">Sign it with this address</label>
          <div class="hashline" id="ctl-sign-address" style="margin-top:6px"><b>${signAddr}</b></div>
          <p class="sub" style="margin:8px 0 0">
            Your wallet's first receiving address. If your wallet marks it as already used, that
            is fine — signing a message spends nothing. If your wallet will only sign with a
            different address, any address it lists as yours will do; put that one in step 3.
          </p>
        </div>
        ${whereToSign()}
      </div>

      <div class="rule"></div>
      <div class="blk">
        <h4>3 · The address you signed with, and the signature</h4>
        <p class="sub">
          A wallet signs with the key for <em>one</em> address. This is filled in with the address
          from step 2 — change it only if your wallet signed with a different one, which it shows
          on its signing screen.
        </p>
        <div class="field">
          <label class="lbl" for="ctl-address">The address you signed with</label>
          <input id="ctl-address" type="text" class="mono" spellcheck="false"
            autocomplete="off" placeholder="${parsed.network === 'mainnet' ? 'bc1q…' : 'tb1q…'}"
            value="${c.address || signAddr}">
        </div>
        <div class="field" style="margin-top:14px">
          <label class="lbl" for="ctl-signature">Signature from your wallet</label>
          <textarea id="ctl-signature" rows="3" class="mono" spellcheck="false"
            placeholder="H1a2b3c...">${c.signature}</textarea>
        </div>
        <button class="btn" id="ctl-build">Build the proof</button>
      </div>` : raw('')}

    ${/*
       * "Send this" appears ONLY for a proof that has already passed the verifier — the same one
       * a stranger runs. The first version showed it for anything that could be serialised, and
       * a proof built around a bad paste went out with a copy button under it.
       */''}
    ${c.proofJson && c.result?.verdict?.ok ? html`
      <div class="rule"></div>
      <div class="blk">
        <h4>4 · Send this</h4>
        <p class="sub">
          Checked here already, with the same verifier the person you send it to will use. Anyone
          can check it, with no account and nothing installed. It contains no private key and
          cannot move any money.
        </p>
        <div class="hashline" id="ctl-proof" style="white-space:pre-wrap;max-height:230px;overflow:auto"
          >${c.proofJson}</div>
        <div class="row" style="margin-top:10px">
          <button class="btn sm" id="ctl-copy-proof">Copy the proof</button>
          <button class="btn sm ghost" id="ctl-download-proof">Save as a file</button>
          <button class="btn sm ghost" id="ctl-selfcheck">Check it again</button>
        </div>
      </div>` : raw('')}
    ${c.proofJson && c.result && !c.result.verdict?.ok ? html`
      <div class="rule"></div>
      <div class="blk">
        <h4>4 · Not ready to send</h4>
        <p class="sub">
          The proof was built and then checked here, with the same verifier the person you would
          send it to will use, and it would be refused. There is nothing to send yet — the reason
          is just below.
        </p>
      </div>` : raw('')}`;
}

function verdictView(): Raw {
  const r = state.control.result;
  if (!r) return raw('');

  if (r.inputError)
    return html`
      <div class="note r" style="margin-top:16px">
        <div class="h">This could not be read</div>
        ${r.inputError}
        <p class="sub" style="margin:8px 0 0">That is a problem with the file, not a verdict about
        the wallet. Nothing has been proved or disproved.</p>
      </div>`;

  const v = r.verdict!;
  if (!v.ok) {
    const rejected = v.rejected as { keyIndex: number; code: string; reason: string }[];
    return html`
      <div class="note r" style="margin-top:16px">
        <div class="h">Not proved</div>
        <p style="margin-bottom:8px">${plainFailure(v)}</p>
        ${rejected.length > 0 ? html`
          <p class="sub" style="margin-bottom:0">
            ${rejected.length === 1 ? 'The signature' : `${String(rejected.length)} signatures`}
            ${rejected.length === 1 ? 'was' : 'were'} refused — the reason is below.
          </p>` : raw('')}
      </div>
      ${rejected.length > 0 ? html`
        <div class="card tight" style="margin-top:12px">
          <h4>Why the signature${rejected.length > 1 ? 's were' : ' was'} refused</h4>
          ${raw(rejected.map((x, i) => `
            <div class="dr"><dt>${rejected.length > 1 ? `Signature ${String(i + 1)}` : 'Reason'}</dt>
            <dd style="text-align:left;max-width:52ch;font-family:var(--serif);font-size:13px">${
              escapeAttr(PLAIN_REJECTION[x.code] ?? x.reason)}</dd></div>`).join(''))}
        </div>` : raw('')}
      ${/* The engine's own words, one disclosure away — an auditor checks a specific statement. */''}
      <details class="tech" style="margin-top:10px">
        <summary>Technical detail</summary>
        <p class="mono" style="font-size:11.5px;margin-top:8px">${v.failure ?? ''}</p>
        ${raw(rejected.map((x) => `<p class="mono" style="font-size:11.5px;margin-top:4px">slot ${
          String(x.keyIndex)} · ${escapeAttr(x.code)} · ${escapeAttr(x.reason)}</p>`).join(''))}
      </details>`;
  }

  return html`
    <div class="note f" style="margin-top:16px">
      <div class="h">Control proved</div>
      <p style="margin-bottom:10px">
        The keys for this wallet signed a challenge they could not have prepared in advance, and
        the wallet controls this address:
      </p>
      <div class="hashline" style="margin-top:0"><b>${v.address ?? 'unknown'}</b></div>
      <p class="sub" style="margin:10px 0 0">
        ${v.n === 1
          ? 'The wallet\u2019s one key signed.'
          : `${String(v.satisfied)} of the ${String(v.m)} required keys signed, out of ${String(v.n)}.`}
        Checked in this page, against the wallet description carried in the proof itself.
      </p>
    </div>`;
}

/**
 * What each refusal means to the person reading it. The engine's reason names a key slot and a
 * derivation path, which is exactly what an auditor needs and nothing a counterparty can act
 * on. The person checking a proof is the least technical person in the ceremony — they were
 * sent a file and want to know whether to believe it — so the plain sentence leads and the
 * engine's words sit one disclosure down.
 */
const PLAIN_REJECTION: Record<string, string> = {
  SIGNATURE_MALFORMED:
    'This is not a signature a wallet would produce. It was probably copied incompletely, or ' +
    'something other than the signature was pasted in.',
  KEY_NOT_IN_DESCRIPTOR:
    'The signature was not made by this wallet\u2019s key for the address named. The usual ' +
    'causes: the message was changed before it was signed (a stray space, a missing ' +
    'character), it was signed with a different address than the one named, or the proof was ' +
    'edited after it was made.',
  KEY_INDEX_OUT_OF_RANGE: 'The proof names a key this wallet does not have.',
  DERIVATION_INVALID: 'The proof names an address position that cannot exist.',
  DERIVE_FAILED:
    'The wallet description inside the proof could not produce the key the proof names.',
  SCHEME_UNSUPPORTED:
    'The signature is in a format this page cannot check yet. Only the standard "Bitcoin ' +
    'Signed Message" format is checked here.',
  DUPLICATE_SLOT: 'The same key signed more than once. A repeat does not count again.',
};

/** The verdict's top line, in plain words, from the engine's failure string. */
function plainFailure(v: { failure: string | null; m: number; n: number; satisfied: number }): string {
  const f = v.failure ?? '';
  let m: RegExpMatchArray | null;
  if ((m = f.match(/expired at (\d+)/)))
    return `The challenge expired on ${fmt(Number(m[1]))} UTC. A proof cannot answer an expired ` +
      'challenge — ask for a fresh one and sign again.';
  if ((m = f.match(/not valid until (\d+)/)))
    return `The challenge is not valid until ${fmt(Number(m[1]))} UTC.`;
  if (/issued for descriptor/.test(f))
    return 'The challenge was written for a different wallet from the one this proof describes, ' +
      'so it answers nothing about this wallet.';
  if (/carries no signatures/.test(f))
    return 'The proof contains no signatures, so it shows nothing.';
  if (/spread across/.test(f))
    return 'The signatures name different addresses. A proof of control has to name one.';
  if (/descriptor did not parse/.test(f))
    return 'The wallet description inside the proof could not be read, so there is nothing to ' +
      'check the signatures against.';
  if (/of the required/.test(f))
    return v.n === 1
      ? 'The wallet\u2019s key did not sign this challenge. Nothing here shows control of the wallet.'
      : `${String(v.satisfied)} of the ${String(v.m)} keys this wallet requires signed; the ` +
        'rest were refused, so the wallet\u2019s rule was not met.';
  return f || 'This bundle does not prove control.';
}

/** Local escape for the one place raw() is used, so a reason string cannot inject markup. */
const escapeAttr = (s: string): string =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

function checkView(): Raw {
  return html`
    <div class="blk">
      <h4>Check a proof someone sent you</h4>
      <p class="sub">
        You need nothing of your own — no wallet, no account, no descriptor. The proof carries the
        wallet it is about, and everything below is recomputed here from the bundle itself.
      </p>
      <div class="field">
        <label class="lbl" for="ctl-check">Paste the proof</label>
        <textarea id="ctl-check" rows="9" class="mono" spellcheck="false"
          placeholder='{ "challenge": { ... }, "descriptor": "...", "signatures": [ ... ] }'
          >${state.control.checkText}</textarea>
      </div>
      <button class="btn" id="ctl-verify">Check it</button>
      ${verdictView()}
    </div>`;
}

export function controlView(): Raw {
  const mode = state.control.mode;
  return html`
    <section class="pane">
      <p class="eyebrow">Proof of control</p>
      ${/*
         * The heading follows the MODE. It read "Prove the wallet is yours" above a screen whose
         * next line is "You need nothing of your own" — the reader is checking someone else's
         * claim there, not making one, and a heading that describes the other person's job is
         * how a control surface starts to feel like it is talking past you.
         */''}
      ${mode === 'prove'
        ? html`
          <h2>Prove the wallet is yours</h2>
          <p class="sub" style="margin-bottom:18px">
            A signature made by the keys behind a wallet, over a question only the asker could
            have written. It shows the wallet is controlled — it moves no money, exposes no key,
            and anyone can check it without trusting you or this page.
          </p>`
        : html`
          <h2>Check a proof of control</h2>
          <p class="sub" style="margin-bottom:18px">
            Somebody claims a bitcoin wallet is theirs. This works out whether the keys behind
            that wallet really signed the challenge they were given, and names the address they
            control — without trusting them, and without trusting this page either: every number
            below is recomputed here from the proof itself.
          </p>`}

      <div class="viewas">
        <div class="seg" role="group" aria-label="What are you doing">
          <button type="button" data-ctl-mode="prove"
            aria-pressed="${mode === 'prove' ? 'true' : 'false'}">I hold the wallet</button>
          <button type="button" data-ctl-mode="check"
            aria-pressed="${mode === 'check' ? 'true' : 'false'}">I was sent a proof</button>
        </div>
      </div>

      ${state.control.error
        ? html`<div class="note r"><div class="h">That did not work</div>${state.control.error}</div>`
        : raw('')}

      ${mode === 'prove' ? proveView() : checkView()}
      ${mode === 'prove' && state.control.result ? verdictView() : raw('')}
    </section>`;
}

export { DEFAULT_TTL_MS };
